<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    Bell,
    CheckCircle,
    AlertCircle,
    AlertTriangle,
    Info,
    Clock,
    UserPlus,
    Download
} from 'lucide-vue-next';

const props = defineProps({
    notification: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['click']);

const { t } = useI18n();

const isUnread = computed(() => !props.notification.read_at);

const iconConfig = computed(() => {
    const data = props.notification.data || {};
    const severity = data.severity || 'info';
    const type = data.type || props.notification.type;

    // Icon based on notification type
    if (type === 'utilization_recalculation' || type === 'UtilizationRecalculationNotification') {
        return {
            icon: data.status === 'completed' ? CheckCircle : Clock,
            class: data.status === 'completed' ? 'text-green-500' : 'text-blue-500',
            bg: data.status === 'completed' ? 'bg-green-500/10' : 'bg-blue-500/10'
        };
    }
    
    if (type === 'clock_event' || type === 'ClockEventNotification') {
        return {
            icon: severity === 'error' ? AlertCircle : Bell,
            class: severity === 'error' ? 'text-red-500' : 'text-blue-500',
            bg: severity === 'error' ? 'bg-red-500/10' : 'bg-blue-500/10'
        };
    }

    if (type === 'employee_invitation' || type === 'EmployeeInvitationNotification') {
        return {
            icon: UserPlus,
            class: 'text-purple-500',
            bg: 'bg-purple-500/10'
        };
    }

    if (type === 'activity_logs_export' || type === 'ActivityLogsExportNotification') {
        const isCompleted = data.status === 'completed';
        return {
            icon: Download,
            class: isCompleted ? 'text-green-500' : 'text-blue-500',
            bg: isCompleted ? 'bg-green-500/10' : 'bg-blue-500/10'
        };
    }

    if (type === 'alert_triggered' || type === 'AlertTriggeredNotification') {
        return {
            icon: AlertTriangle,
            class: 'text-amber-500',
            bg: 'bg-amber-500/10'
        };
    }

    // Default based on severity
    const severityMap = {
        success: { icon: CheckCircle, class: 'text-green-500', bg: 'bg-green-500/10' },
        error: { icon: AlertCircle, class: 'text-red-500', bg: 'bg-red-500/10' },
        warning: { icon: AlertCircle, class: 'text-yellow-500', bg: 'bg-yellow-500/10' },
        info: { icon: Info, class: 'text-blue-500', bg: 'bg-blue-500/10' }
    };

    return severityMap[severity] || severityMap.info;
});

const notificationTitle = computed(() => {
    const data = props.notification.data || {};
    const type = data.type || props.notification.type;

    const typeMap = {
        'utilization_recalculation': t('notifications.types.utilization_recalculation'),
        'UtilizationRecalculationNotification': t('notifications.types.utilization_recalculation'),
        'clock_event': t('notifications.types.clock_event'),
        'ClockEventNotification': t('notifications.types.clock_event'),
        'employee_invitation': t('notifications.types.employee_invitation'),
        'EmployeeInvitationNotification': t('notifications.types.employee_invitation'),
        'activity_logs_export': t('notifications.types.activity_logs_export'),
        'ActivityLogsExportNotification': t('notifications.types.activity_logs_export'),
        'alert_triggered': t('alerts.notification_bell.alert_triggered'),
        'AlertTriggeredNotification': t('alerts.notification_bell.alert_triggered')
    };

    return typeMap[type] || type;
});

const notificationMessage = computed(() => {
    const data = props.notification.data || {};
    const type = data.type || props.notification.type;

    if (type === 'alert_triggered' || type === 'AlertTriggeredNotification') {
        const alertName = data.alert_name || '';
        const employeeName = data.employee_name || '';
        const summary = data.snapshot_summary || data.message || '';
        if (employeeName && summary) {
            return `${employeeName} — ${summary}`;
        }
        if (alertName) return alertName;
        return summary || data.message || '';
    }

    return data.message || data.formula_name || '';
});

const timeAgo = computed(() => {
    const createdAt = new Date(props.notification.created_at);
    const now = new Date();
    const diffMs = now - createdAt;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
        return t('notifications.ago.just_now');
    } else if (diffMins < 60) {
        return t('notifications.ago.minutes', { count: diffMins });
    } else if (diffHours < 24) {
        return t('notifications.ago.hours', { count: diffHours });
    } else {
        return t('notifications.ago.days', { count: diffDays });
    }
});

const handleClick = () => {
    emit('click', props.notification);
};
</script>

<template>
    <button
        @click="handleClick"
        class="w-full flex items-start gap-3 p-3 text-left transition-all hover:bg-accent/30 rounded-lg group relative"
        :class="isUnread ? 'border-l-2 border-l-primary bg-primary/5' : 'border-l-2 border-l-transparent'"
    >
        <!-- Icon -->
        <div 
            class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
            :class="iconConfig.bg"
        >
            <component :is="iconConfig.icon" class="w-5 h-5" :class="iconConfig.class" />
        </div>

        <!-- Content -->
        <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2">
                <p class="font-medium text-sm text-foreground truncate">
                    {{ notificationTitle }}
                </p>
                <!-- Unread indicator dot -->
                <div 
                    v-if="isUnread" 
                    class="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-primary"
                    :aria-label="t('notifications.unread_aria')"
                />
            </div>
            <p 
                v-if="notificationMessage" 
                class="text-xs text-muted-foreground mt-0.5 line-clamp-2"
            >
                {{ notificationMessage }}
            </p>
            <p class="text-xs text-muted-foreground/70 mt-1">
                {{ timeAgo }}
            </p>
        </div>
    </button>
</template>
